import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useLayoutEffect
} from 'react';
import { buildEngine, nearbyColumnEngine } from '../../engine/Engine';
import SearchContext from '../../state/context';
import { buildResultList } from '@coveo/headless';
import useEventBus from '../../../hooks/useEventBus';
import useIsVisible from '../../../hooks/useIsVisible';
import {
  getLocationCookie,
  sendCoveoGeolocationInterfaceLoad,
  getCoveoFindACommunityFieldsToInclude,
  unitConversion
} from '../../helpers';
import NearbyCommunitiesColumnList from './NearbyCommunitiesColumnList';
import Cookies from 'js-cookie';

const NearbyCommunitiesSupportingColumn = props => {
  const {
    locationCookieName,
    coveoOrganizationId,
    coveoAccessToken,
    coveoSearchHub,
    coveoPipeline,
    coveoSource = 'Production US - English',
    coveoContextLanguage = 'en',
    coveoSearchRadiusInMiles,
    distanceLabel,
    coveoNumberOfResults,
    coveoUnit,
    recentlyViewedTitle,
    recentlyViewedMoreLabel,
    recentlyViewedMoreLink,
    nearbyTitle,
    nearbyMoreLabel,
    nearbyMoreLink
  } = props;
  const elementRef = useRef(null);
  const [waitingForGeolocation, setWaitingForGeolocation] = useState(true);
  const [geolocationData, setGeolocationData] = useState(null);

  const firstVisible = useRef(true);
  const isCommunityPage = useRef(false);
  const recentCommunitiesWrapper = useRef(null);
  const columnCommunitiesMoreLink = useRef(null);
  const columnCommunitiesTitle = useRef(null);
  const panel = useRef(null);
  const communityData = useRef(null);
  const currentCommunity = useRef(null);
  const recentlyViewedCommunities = useRef(null);

  const isVisible = useIsVisible(elementRef);

  const engine = useMemo(() => {
    return buildEngine({
      organizationId: coveoOrganizationId,
      accessToken: coveoAccessToken,
      searchHub: coveoSearchHub,
      pipeline: coveoPipeline
    });
  }, []);

  const resultsListController = useMemo(
    () =>
      buildResultList(engine, {
        options: {
          fieldsToInclude: getCoveoFindACommunityFieldsToInclude()
        }
      }),
    []
  );

  const getCommunity = () => {
    if (currentCommunity.current && panel.current) {
      const {
        ssl_communityname,
        ssl_thumbnailimage,
        ssl_mainimagedesktop,
        ssl_pricingandavailabilityphone,
        ssl_residentsandfamilyphone,
        ssl_distance,
        ssl_communitylink
      } = currentCommunity.current;
      const communityHeader = document.querySelector(
        '.schedule-a-tour-current-community__card-name'
      );
      const communityImage = document.querySelector(
        '.schedule-a-tour-current-community__image'
      );
      const availabilityPhone = document.querySelector(
        '.schedule-a-tour__availability-contact'
      );
      const residentsPhone = document.querySelector(
        '.schedule-a-tour__residents-contact'
      );
      const communityLink = document.querySelector(
        '.schedule-a-tour-current-community__card'
      );
      const title = document.querySelector(
        '.schedule-a-tour-current-community__contacts-title'
      );

      if (ssl_communityname && ssl_communitylink) {
        communityLink.href = ssl_communitylink;
        communityHeader.textContent = ssl_communityname;
        communityImage.src = ssl_thumbnailimage || ssl_mainimagedesktop;
        communityLink.alt = ssl_communityname;
        if (availabilityPhone) {
          availabilityPhone.querySelector('.btn__label').textContent =
            ssl_pricingandavailabilityphone;
          availabilityPhone.href = `tel:${ssl_pricingandavailabilityphone}`;

          if (ssl_pricingandavailabilityphone) {
            availabilityPhone.style.display = 'flex';
          } else {
            availabilityPhone.style.display = 'none';
          }
        }
        if (residentsPhone) {
          residentsPhone.querySelector('.btn__label').textContent =
            ssl_residentsandfamilyphone;
          residentsPhone.href = `tel:${ssl_residentsandfamilyphone}`;

          if (ssl_residentsandfamilyphone) {
            residentsPhone.style.display = 'flex';
          } else {
            residentsPhone.style.display = 'none';
          }
        }

        if (!availabilityPhone && !residentsPhone && title) {
          title.style.display = 'none';
        } else {
          title.style.display = 'unset';
        }
      } else {
        if (!availabilityPhone) availabilityPhone.style.display = 'none';
        if (!residentsPhone) residentsPhone.style.display = 'none';
        if (!availabilityPhone && !residentsPhone && title)
          title.style.display = 'none';
      }
      if (ssl_distance) {
        const communityDistance = document.querySelector(
          '.schedule-a-tour-current-community__card-distance'
        );
        communityDistance.textContent = `${unitConversion(
          ssl_distance,
          distanceLabel
        )} ${panel.current?.dataset.unitLabel}`;
      }
    }
  };

  const recentCommunitiesHandler = () => {
    if (!isCommunityPage.current) {
      if (
        recentlyViewedCommunities.current &&
        recentlyViewedCommunities.current.length > 2
      ) {
        const recentCommunitiesWrapper =
          elementRef.current.parentElement.nextElementSibling.querySelector(
            '.primary-nav__recently-viewed-communities '
          );
        recentCommunitiesWrapper.innerHTML = '';
        recentlyViewedCommunities.current.reverse();
        recentlyViewedCommunities.current.map(el => {
          if (el.ssl_communityid !== currentCommunity.current.ssl_communityid) {
            const communityLink = document.createElement('a');
            communityLink.href = el.ssl_communitylink;
            communityLink.classList.add(
              'primary-nav__recently-viewed-communities-link'
            );
            const communityImage = document.createElement('img');
            communityImage.src = `${el.ssl_thumbnailimage}`;
            communityImage.classList.add(
              'primary-nav__recently-viewed-communities-image'
            );
            const communityInfo = document.createElement('div');
            communityInfo.classList.add(
              'primary-nav__recently-viewed-communities-info'
            );
            const communityTitle = document.createElement('span');
            communityTitle.classList.add(
              'primary-nav__recently-viewed-communities-title'
            );
            communityTitle.textContent = el.ssl_communityname;
            const communityDistance = document.createElement('span');
            communityDistance.classList.add(
              'primary-nav__recently-viewed-communities-distance'
            );
            communityDistance.textContent = el.ssl_distance
              ? `${unitConversion(el.ssl_distance, distanceLabel)} ${
                  panel.current?.dataset.unitLabel
                }`
              : '';
            communityInfo.append(communityTitle);
            communityInfo.append(communityDistance);
            communityLink.append(communityImage);
            communityLink.append(communityInfo);
            recentCommunitiesWrapper.append(communityLink);
          }
          recentCommunitiesWrapper.classList.remove('hidden');
          if (columnCommunitiesTitle)
            columnCommunitiesTitle.current.innerHTML = recentlyViewedTitle;
          if (columnCommunitiesMoreLink) {
            columnCommunitiesMoreLink.current.querySelector(
              'span'
            ).textContent = recentlyViewedMoreLabel;
            columnCommunitiesMoreLink.href = recentlyViewedMoreLink;
          }
        });
      }
    } else {
      const communitiesColumn = document.querySelector(
        '.schedule-a-tour__content'
      )?.nextElementSibling;
      if (communitiesColumn) communitiesColumn.remove();
    }
  };

  const getTitle = () => {
    if (
      recentlyViewedCommunities.current &&
      recentlyViewedCommunities.current.length < 2 &&
      resultsListController.state.results.length > 0
    )
      return nearbyTitle;
    else if (
      recentlyViewedCommunities.current &&
      recentlyViewedCommunities.current.length > 2
    )
      return recentlyViewedTitle;
    else if (
      !recentlyViewedCommunities.current &&
      resultsListController.state.results.length > 0
    )
      return nearbyTitle;
  };

  const getMoreLink = () => {
    if (
      recentlyViewedCommunities.current &&
      recentlyViewedCommunities.current.length < 2 &&
      resultsListController.state.results.length > 0
    )
      return nearbyMoreLink;
    else if (
      recentlyViewedCommunities.current &&
      recentlyViewedCommunities.current.length > 2
    )
      return recentlyViewedMoreLink;
    else if (
      !recentlyViewedCommunities.current &&
      resultsListController.state.results.length > 0
    )
      return nearbyMoreLink;
  };

  const getMoreLabel = () => {
    if (
      recentlyViewedCommunities.current &&
      recentlyViewedCommunities.current.length < 2 &&
      resultsListController.state.results.length > 0
    )
      return nearbyMoreLabel;
    else if (
      recentlyViewedCommunities.current &&
      recentlyViewedCommunities.current.length > 2
    )
      return recentlyViewedMoreLabel;
    else if (
      !recentlyViewedCommunities.current &&
      resultsListController.state.results.length > 0
    )
      return nearbyMoreLabel;
  };

  useEffect(() => {
    if (firstVisible.current && isVisible) {
      firstVisible.current = false;
      if (geolocationData) {
        sendCoveoGeolocationInterfaceLoad({
          engine: engine,
          latitude: geolocationData.Latitude,
          longitude: geolocationData.Longitude,
          source: coveoSource,
          radiusInMiles: parseFloat(coveoSearchRadiusInMiles),
          language: coveoContextLanguage,
          pipeline: coveoPipeline
        });
      }
    }
  }, [isVisible]);

  useEventBus(
    'geolocator-loaded',
    () => {
      let cookieData = getLocationCookie(locationCookieName);
      if (cookieData) {
        setGeolocationData(cookieData);
        //if (!resultsListController.state.firstSearchExecuted)
        // sendCoveoGeolocationInterfaceLoad({
        //   engine: engine,
        //   latitude: cookieData.Latitude,
        //   longitude: cookieData.Longitude,
        //   source: coveoSource,
        //   radiusInMiles: parseFloat(coveoSearchRadiusInMiles),
        //   language: coveoContextLanguage,
        //   pipeline: coveoPipeline
        // });
      } else {
        const changeCommunityLink = document.querySelector(
          '.schedule-a-tour-current-community__cta'
        );
        panel.current = document.querySelector('.schedule-a-tour');
        changeCommunityLink.href =
          panel?.current.dataset.changeCommunityLinkNoLocation;
      }
      setWaitingForGeolocation(false);
    },
    []
  );

  useEffect(() => {
    if (!elementRef) return;
    columnCommunitiesTitle.current = elementRef.current.parentElement
      .closest('.primary-nav__supporting-column-inner')
      .querySelector('.primary-nav__supporting-column-title');
    columnCommunitiesMoreLink.current = elementRef.current.parentElement
      .closest('.primary-nav__supporting-column-inner')
      .querySelector('.primary-nav__supporting-column-cta');
    panel.current = document.querySelector('.schedule-a-tour');
    const communityPage = document.querySelector(
      '.community-landing-page-coveo'
    );
    recentCommunitiesWrapper.current = elementRef.current.nextElementSibling;
    if (communityPage) isCommunityPage.current = true;
    if (isCommunityPage.current) {
      communityData.current = {};
      communityData.current.ssl_latitude = communityPage.dataset.sslLatitude;
      communityData.current.ssl_longitude = communityPage.dataset.sslLongitude;
      communityData.current.ssl_communityname =
        communityPage.dataset.sslCommunityname;
      communityData.current.ssl_thumbnailimage =
        communityPage.dataset.sslThumbnailimage;
      communityData.current.ssl_mainimagedesktop =
        communityPage.dataset.sslMainimagedesktop;
      communityData.current.ssl_pricingandavailabilityphone =
        communityPage.dataset.sslPricingandavailabilityphone;
      communityData.current.ssl_residentsandfamilyphone =
        communityPage.dataset.sslResidentsandfamilyphone;
      communityData.current.ssl_contextlanguage =
        communityPage.dataset.sslContextlanguage;
      communityData.current.ssl_communityid =
        communityPage.dataset.sslCommunityid;
      communityData.current.communityUrl = communityPage.dataset.communityUrl;
    }

    if (Cookies.get('current_community')) {
      currentCommunity.current = JSON.parse(Cookies.get('current_community'));
    } else if (
      isCommunityPage.current &&
      currentCommunity.current?.ssl_communityid
    ) {
      currentCommunity.current = {
        ssl_latitude: communityData.ssl_latitude,
        ssl_longitude: communityData.ssl_longitude,
        ssl_communityname: communityData.communityName,
        ssl_thumbnailimage: communityData.ssl_thumbnailimage
          ? communityData.ssl_thumbnailimage
          : communityData.ssl_mainimagedesktop,
        ssl_pricingandavailabilityphone:
          communityData.ssl_pricingandavailabilityphone,
        ssl_residentsandfamilyphone: communityData.ssl_residentsandfamilyphone,
        ssl_contextlanguage: communityData.ssl_contextlanguage,
        ssl_communityid: communityData.ssl_communityid,
        ssl_communitylink: communityData.communityUrl
      };
    }

    // // Recently viewed communities
    if (Cookies.get('recent_communities')) {
      recentlyViewedCommunities.current = JSON.parse(
        Cookies.get('recent_communities')
      );
    }

    recentCommunitiesHandler();
    getCommunity();

    resultsListController.subscribe(() => {
      if (resultsListController.state && elementRef.current) {
        if (resultsListController.state.results.length === 0) {
          elementRef.current.parentElement.parentElement
            .querySelector(
              '.nearby-communities-supporting-column-headless__default-items > .primary-nav__recently-viewed-communities'
            )
            .classList.remove('hidden');
        } else {
          if (
            (recentlyViewedCommunities.current &&
              recentlyViewedCommunities.current.length < 2) ||
            !recentlyViewedCommunities.current
          )
            elementRef.current.parentElement.parentElement
              .querySelector(
                '.nearby-communities-supporting-column-headless__default-items > .primary-nav__recently-viewed-communities'
              )
              .classList.add('hidden');

          const recentCommunitiesWrapper = elementRef.current
            .closest('.nearby-communities-supporting-column-headless')
            .parentElement.querySelector(
              '.primary-nav__supporting-column-wrapper > .primary-nav__supporting-column-inner > .primary-nav__recently-viewed-communities'
            );

          if (recentCommunitiesWrapper)
            recentCommunitiesWrapper.classList.add('hidden');

          if (columnCommunitiesTitle)
            columnCommunitiesTitle.current.innerHTML = getTitle();

          if (columnCommunitiesMoreLink) {
            columnCommunitiesMoreLink.current.querySelector(
              'span'
            ).textContent = getMoreLabel();
            columnCommunitiesMoreLink.href = getMoreLink();
          }
        }
      }
    });
  }, [elementRef.current]);

  return (
    <div ref={elementRef}>
      <SearchContext.Provider value={{ engine: engine }}>
        <>
          {((recentlyViewedCommunities.current &&
            recentlyViewedCommunities.current.length < 2) ||
            !recentlyViewedCommunities.current) && (
            <NearbyCommunitiesColumnList
              controller={resultsListController}
              maxResults={coveoNumberOfResults}
              distanceLabel={distanceLabel}
              coveoUnit={coveoUnit}
            />
          )}
        </>
      </SearchContext.Provider>
    </div>
  );
};

export default NearbyCommunitiesSupportingColumn;
